import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Users from 'Api/Users';

/**
 * @typedef {Object} UsePinnedStudentsReturn
 * @property {Array<{value: string, label: string}>} data
 * @property {Array<{userId?: string, id?: string}>} rawData
 * @property {boolean} loading
 * @property {Error|null} error
 * @property {() => Promise<void>} updatePinnedStudents
 * @property {() => void} refetch
 */

/**
 * Hook to load pinned students for the current teacher.
 * Uses Users.getPinnedStudents API.
 * @returns {UsePinnedStudentsReturn}
 */
const usePinnedStudents = () => {
    const teacherId = useSelector((state) => state.auth?.teacher?.id);
    const [data, setData] = useState([]);
    const [rawData, setRawData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!teacherId) {
                setData([]);
                setRawData([]);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const raw = await Users.getPinnedStudents({ userId: teacherId });
                if (cancelled) return;
                const rawList = Array.isArray(raw) ? raw : [];
                setRawData(rawList);
                const studentOptions = rawList.map((user) => {
                    const userId = user?.userId ?? user?.id ?? '';
                    const label =
                        [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
                        user?.firstName ||
                        user?.lastName ||
                        String(userId) ||
                        'Unknown';
                    return { value: String(userId), label };
                });
                const options =
                    studentOptions.length > 0
                        ? [{ value: 'all', label: 'All Pinned Students' }, ...studentOptions]
                        : [];
                setData(options);
            } catch (err) {
                if (!cancelled) {
                    setError(err);
                    setData([]);
                    setRawData([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [teacherId, refreshTrigger]);

    const refetch = useCallback(() => {
        setRefreshTrigger((t) => t + 1);
    }, []);

    const updatePinnedStudents = useCallback(
        async (selectedUserIds) => {
            if (!teacherId) return;
            const currentPinnedIds = new Set(
                (rawData || []).map((u) => String(u?.userId ?? u?.id ?? '')).filter(Boolean)
            );
            const selectedSet = new Set((selectedUserIds || []).map(String).filter(Boolean));

            const toAdd = [...selectedSet].filter((id) => !currentPinnedIds.has(id));
            const toRemove = [...currentPinnedIds].filter((id) => !selectedSet.has(id));

            for (const userId of toRemove) {
                await Users.handlePinningStudent(userId);
            }
            for (const userId of toAdd) {
                await Users.handlePinningStudent(userId);
            }

            if (toAdd.length > 0 || toRemove.length > 0) {
                refetch();
            }
        },
        [teacherId, rawData, refetch]
    );

    return { data, rawData, loading, error, updatePinnedStudents, refetch };
};

export default usePinnedStudents;
